import { ref, shallowRef, Ref } from 'vue';
import { Moment } from 'moment';
import Client from '@/models/Client';
import { fetchDeliveryOrders } from '@/modules/Logistics/helpers/DeliveryOrderHelper';
import {
    buildDeliveryClientRegionFilter,
    buildDeliveryRegionFilter,
} from '@/modules/Logistics/helpers/DeliveryFiltersHelper';
import type { DeliveryClientOption, DeliveryOrder } from '@/modules/Logistics/interfaces/DeliveryOrderInterfaces';
import type { DeliveryTablePagination } from '@/modules/Logistics/interfaces/LogisticsInterfaces';

interface UseDeliveryFiltersOptions {
    selectedOrderIds: Ref<string[]>;
    selectedInternalOrderIds: Ref<number[]>;
    ordersLoading: Ref<boolean>;
    notifyError: (description: string, error: unknown) => void;
}

export function useDeliveryFilters(options: UseDeliveryFiltersOptions) {
    const { selectedOrderIds, selectedInternalOrderIds, ordersLoading, notifyError } = options;

    const selectedDeliveryDates = shallowRef<Moment[]>([]);
    const selectedDeliveryClientId = ref<string | null>(null);
    const selectedOrderDeliveryRegionId = ref<string | null>(null);
    const deliveryClients = ref<DeliveryClientOption[]>([]);
    const deliveryClientSearchLoading = ref(false);
    const availableDeliveryOrders = ref<DeliveryOrder[]>([]);
    const deliveryDateFilter = ref<Moment | null>(null);
    const deliveryTablePagination = ref<DeliveryTablePagination>({
        current: 1,
        pageSize: 10,
        total: 20,
        pageSizeOptions: ['10', '20', '50', '100'],
        showSizeChanger: true,
    });

    function updateDeliveryTablePagination(current: number, pageSize: number) {
        deliveryTablePagination.value.current = current;
        deliveryTablePagination.value.pageSize = pageSize;
        deliveryTablePagination.value.total = (current + 1) * pageSize;
    }

    function updateDeliveryTableTotal(total: number) {
        deliveryTablePagination.value.total = total;
    }

    function updateDeliveryDateFilter(date: Moment | null) {
        deliveryDateFilter.value = date;
        deliveryTablePagination.value.current = 1;
        deliveryTablePagination.value.total = deliveryTablePagination.value.pageSize * 2;
    }

    async function handleDeliveryDatesChange(dates: Moment[]) {
        selectedDeliveryDates.value = dates;

        await refreshDeliveryOrders();
    }

    async function handleDeliveryClientSearch(searchQuery: string) {
        const trimmedSearchQuery = searchQuery.trim();

        if (trimmedSearchQuery.length < 3) {
            deliveryClients.value = [];
            return;
        }

        try {
            deliveryClientSearchLoading.value = true;

            const clients = (await Client.searchAndFilter(
                buildDeliveryClientRegionFilter(selectedOrderDeliveryRegionId.value),
                trimmedSearchQuery,
            )) as Client[];
            deliveryClients.value = createDeliveryClientOptions(clients);
        } catch (error) {
            deliveryClients.value = [];
            notifyError('Failed to fetch clients.', error);
        } finally {
            deliveryClientSearchLoading.value = false;
        }
    }

    async function handleDeliveryClientChange(clientId: string | null) {
        selectedDeliveryClientId.value = clientId;
        await refreshDeliveryOrders();
    }

    async function handleOrderDeliveryRegionChange(deliveryRegionId: string | null) {
        selectedOrderDeliveryRegionId.value = deliveryRegionId;
        await refreshDeliveryOrders();
    }

    async function refreshDeliveryOrders() {
        // selectedOrderIds.value = [];
        // selectedInternalOrderIds.value = [];

        if (selectedDeliveryDates.value.length === 0) {
            availableDeliveryOrders.value = [];
            return;
        }

        try {
            ordersLoading.value = true;
            availableDeliveryOrders.value = [];
            availableDeliveryOrders.value = await fetchDeliveryOrders({
                deliveryDates: selectedDeliveryDates.value,
                clientId: selectedDeliveryClientId.value,
                deliveryRegionId: selectedOrderDeliveryRegionId.value,
            });
        } catch (error) {
            notifyError('Failed to fetch orders for the selected date.', error);
        } finally {
            ordersLoading.value = false;
        }
    }

    function resetDeliveryFilters() {
        selectedDeliveryDates.value = [];
        selectedDeliveryClientId.value = null;
        selectedOrderDeliveryRegionId.value = null;
        deliveryClients.value = [];
        availableDeliveryOrders.value = [];
    }

    function createDeliveryClientOptions(clients: Client[]): DeliveryClientOption[] {
        return clients.map((client) => ({
            id: String(client.id),
            name: client.name || '',
        }));
    }

    return {
        selectedDeliveryDates,
        availableDeliveryOrders,
        selectedDeliveryClientId,
        selectedOrderDeliveryRegionId,
        deliveryClients,
        deliveryClientSearchLoading,
        deliveryDateFilter,
        deliveryTablePagination,
        handleDeliveryDatesChange,
        handleDeliveryClientSearch,
        handleDeliveryClientChange,
        handleOrderDeliveryRegionChange,
        updateDeliveryTablePagination,
        updateDeliveryTableTotal,
        updateDeliveryDateFilter,
        resetDeliveryFilters,
    };
}
